import React, { useState } from 'react';
import { X, FileCheck, CheckCircle, AlertTriangle, Loader2, Shield, Download, Lock } from 'lucide-react';
import { toast } from '../../utils/toastUtils';

const API_BASE = `${process.env.REACT_APP_API_URL || ''}/api/v1`;
const getToken = () => localStorage.getItem('access_token');

export default function CertificateModal({ workspaceId, dashboard, onClose }) {
  const [generating, setGenerating] = useState(false);
  const [cert, setCert] = useState(null);

  // Eligibility check — all critical/high must be treated
  const criticalOpen = (dashboard?.by_severity?.critical?.open || 0) + (dashboard?.by_severity?.critical?.in_progress || 0);
  const highOpen = (dashboard?.by_severity?.high?.open || 0) + (dashboard?.by_severity?.high?.in_progress || 0);
  const isEligible = criticalOpen === 0 && highOpen === 0;
  const progress = dashboard?.progress_pct || 0;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/remediation/certificate/${workspaceId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setCert(data);
      toast.success('Certificate generated successfully');
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!cert?.file_path) return;
    const token = getToken();
    const filename = cert.file_path.split('/').pop();
    const response = await fetch(`${API_BASE}/remediation/certificate/${workspaceId}/download/${cert.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) { toast.error('Download unavailable'); return; }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'certificat-cloture.pdf';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-2xl border border-gray-700 w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/15 rounded-lg border border-green-500/30">
              <FileCheck className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Closure certificate</h2>
              <p className="text-xs text-gray-400 mt-0.5">Remediation attestation — NIS2 / DORA / ISO 27001</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Status summary */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 space-y-3">
            <p className="text-xs font-medium text-gray-300">Remediation status</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                { label: 'Total', value: dashboard?.total || 0, color: 'text-gray-300' },
                { label: 'Verified', value: dashboard?.verified || 0, color: 'text-green-400' },
                { label: 'Progression', value: `${progress}%`, color: progress >= 80 ? 'text-green-400' : 'text-yellow-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-gray-900/50 rounded-lg p-2">
                  <p className={`text-xl font-bold ${color}`}>{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Eligibility checklist */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-300">Prerequisites for certificate issuance:</p>
            {[
              {
                label: `Open Critical Vulnerabilities: ${criticalOpen}`,
                ok: criticalOpen === 0,
              },
              {
                label: `Open High Vulnerabilities: ${highOpen}`,
                ok: highOpen === 0,
              },
              {
                label: `Progression globale : ${progress}%`,
                ok: progress >= 50,
              },
            ].map(({ label, ok }) => (
              <div key={label} className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${ok ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                {ok
                  ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  : <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                <span className={`text-xs ${ok ? 'text-green-300' : 'text-red-300'}`}>{label}</span>
              </div>
            ))}
          </div>

          {/* Blocker message */}
          {!isEligible && (
            <div className="flex items-start gap-2.5 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
              <Lock className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-300">
                The certificate can only be issued once all <strong>Critical</strong> and <strong>High</strong> vulnerabilities are addressed (verified, risk accepted or will not be fixed).
              </p>
            </div>
          )}

          {/* Certificate result */}
          {cert && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-sm font-medium text-green-400">Certificate issued</p>
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <p>Reference: <span className="font-mono text-gray-200">{cert.certificate_ref}</span></p>
                <p>Date : <span className="text-gray-200">{new Date().toLocaleDateString('fr-FR')}</span></p>
              </div>
              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-gray-700">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors">
            Fermer
          </button>
          {!cert && (
            <button
              onClick={handleGenerate}
              disabled={!isEligible || generating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {generating
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                : <><Shield className="w-4 h-4" /> Generate certificate</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
